import {
    CircularProgress,
    Container,
    Grid,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography
} from "@mui/material";
import {useApplicantCheck} from "../hooks/useApplicantCheck";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "i18nano";
import {useClientCheck} from "../hooks/useClientCheck";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import React, {useEffect, useMemo, useState} from "react";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {ApplicantParty} from "../api/types/Applicant";
import {ApiService} from "../api/ApiService";
import {useApplicantStore} from "../stores/applicantStore";
import PartyCard from "../components/PartyCard";
import EditClientPartyDialog from "../dialogs/EditClientPartyDialog";
import AgreeDialog from "../dialogs/AgreeDialog";
import BreadcrumbsList from "../components/BreadcrumbsList";

const ClientPartiesPage = () => {
    const {agentId, clientId} = useParams();

    const numericAgentId = useMemo(() => agentId ? parseInt(agentId) : undefined, [agentId]);
    const numericClientId = useMemo(() => clientId ? parseInt(clientId) : undefined, [clientId]);
    const isParamsInvalid = !numericAgentId || !numericClientId;

    const navigate = useNavigate();
    const t = useTranslation();

    const {applicant} = useApplicantCheck(numericAgentId);
    const {client, loading, refresh} = useClientCheck(numericClientId);
    const applicantStore = useApplicantStore();

    useEffect(() => {
        if (numericClientId) {
            refresh(numericClientId);
        }
    }, []);

    const handleEditClick = async (party: ApplicantParty) => {
        if (isParamsInvalid || !applicantStore.client) return;

        const response = await ApiService.UpdateClientParty(numericAgentId, numericClientId, party.id, {name: party.name});
        if (response.success) {
            const updatedParties = applicantStore.client.parties.map((p: ApplicantParty) =>
                    p.id === party.id ? {...p, name: party.name} : p
            );
            applicantStore.setClient({
                ...applicantStore.client,
                parties: updatedParties
            });
        }
    }

    const handleDeleteClick = async (party: ApplicantParty) => {
        if (isParamsInvalid) return;

        const response = await ApiService.DeleteClientParty(numericAgentId, numericClientId, party.id);
        if (response.success) {
            if (!applicantStore.client) return;
            applicantStore.setClient({
                ...applicantStore.client,
                parties: applicantStore.client.parties.filter(p => p.id !== party.id)
            });
        }
    }

    // const handleCreateClick = async (payload: CreateClientPartyDialogState) => {
    //     if (isParamsInvalid) return;
    //     const response = await ApiService.CreateClientParty(numericAgentId, numericClientId, payload);
    //     if (response.success) {
    //         refresh(numericClientId);
    //     }
    // };

    if (isParamsInvalid) return;

    const renderContent = () => {
        if (loading) {
            return <CircularProgress sx={{position: "absolute", left: "calc(50% - 20px)", top: "30%"}}/>
        } else if (!client) {
            return <Typography variant={"h5"}>{t("You don't have access to this client")}</Typography>
        } else {
            return <Grid container spacing={3} alignItems="stretch" justifyContent={"center"}>
                {client.parties.map((party) => {
                    const menu = <PartyMenu party={party} onEditClick={handleEditClick}
                                            onDeleteClick={handleDeleteClick}/>

                    return (<Grid item
                                  key={party.id}
                                  xs={12}
                                  sm={6}
                                  md={3}
                                  display={"flex"}
                            >
                                <PartyCard party={party}
                                           onClick={() => navigate(`/agent/${agentId}/clients/${clientId}/parties/${party.id}`)}
                                           // actionMenu={menu}
                                />
                            </Grid>
                    )
                })}

                {/*<Grid item xs={12} sm={6} md={3}>*/}
                {/*    <CreateClientPartyDialog onCreate={handleCreateClick}*/}
                {/*                             agentId={numericAgentId}>*/}
                {/*        {*/}
                {/*            (open, _) => (<CreateClientPartyCardButton onClick={open}/>)*/}
                {/*        }*/}
                {/*    </CreateClientPartyDialog>*/}
                {/*</Grid>*/}
            </Grid>
        }
    }


    return (
            <>
                <Container sx={{paddingTop: 2}}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <BreadcrumbsList
                                    links={[
                                        {text: applicant?.name ?? "", href: `/parties/${applicant?.id}`},
                                        {text: "Clients", href: `/agent/${applicant?.id}/clients`}
                                    ]}
                                    lastItem={{text: client?.name ?? ""}}/>
                        </Grid>
                        <Grid item xs={12}>
                            {renderContent()}
                        </Grid>
                    </Grid>
                </Container>
            </>
    )
}

export default ClientPartiesPage;


interface PartyMenuProps {
    onEditClick: (party: ApplicantParty) => void;
    onDeleteClick: (party: ApplicantParty) => void;
    party: ApplicantParty;
}

const PartyMenu = ({party, onEditClick, onDeleteClick}: PartyMenuProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleEditClick = async (editedParty: ApplicantParty) => {
        if (onEditClick) {
            await onEditClick(editedParty);
        }
        handleClose();
    };

    const handleDeleteClick = async () => {
        if (onDeleteClick) {
            await onDeleteClick(party);
        }
        handleClose();
    }

    return (
            <>
                <IconButton onClick={handleClick}>
                    <MoreVertIcon/>
                </IconButton>
                <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                >
                    <EditClientPartyDialog
                            onEdit={(o) => handleEditClick({...party, name: o.name})}
                            party={party}>
                        {
                            (open, _) => (
                                    <MenuItem onClick={open}>
                                        <ListItemIcon>
                                            <EditIcon fontSize="small"/>
                                        </ListItemIcon>
                                        <ListItemText>Edit</ListItemText>
                                    </MenuItem>
                            )

                        }
                    </EditClientPartyDialog>
                    <AgreeDialog onDelete={handleDeleteClick}
                                 text={`Do you really want to delete this party (${party.name})?`}>
                        {
                            (open, _) => (
                                    <MenuItem onClick={open} color={"red"} sx={{color: 'error.main'}}>
                                        <ListItemIcon sx={{color: 'inherit'}}>
                                            <DeleteIcon fontSize="small"/>
                                        </ListItemIcon>
                                        <ListItemText>Delete</ListItemText>
                                    </MenuItem>
                            )
                        }
                    </AgreeDialog>
                </Menu>
            </>
    )
}